import {EventEmitter, Injectable} from '@angular/core';
import {ScreenConfig} from '../../configs/screen.config';
import {TBreakpointSystem, TDeviceSystem} from '../../interfaces/system.interface';

type TKey = TBreakpointSystem | TDeviceSystem;

export class Responsive {
  current: TBreakpointSystem = 'xs';
  msgError: string = `Can't find breakpoint or device`;

  // Subscription
  currentChanges: EventEmitter<TBreakpointSystem> = new EventEmitter<TBreakpointSystem>();

  constructor() {}

  up(value: TKey) {
    const self = (this.up as any);
    const cache = `${value}_${this.current}`;

    if (self.hasOwnProperty(cache))
      return self[cache];

    const bp = ScreenConfig[value] && ScreenConfig[value][0];
    const key = bp || (ScreenConfig.breakpoints.hasOwnProperty(value) && value);

    if (!key)
      throw(this.msgError);

    const values = {
      key: ScreenConfig.breakpoints[key],
      current: ScreenConfig.breakpoints[this.current]
    };

    self[cache] = values.key <= values.current;

    return self[cache];
  }

  down(value: TKey) {
    const self = (this.down as any);
    const cache = `${value}_${this.current}`;

    if (self.hasOwnProperty(cache))
      return self[cache];

    const bp = ScreenConfig[value] && ScreenConfig[value].slice(-1)[0];
    const key = bp || (ScreenConfig.breakpoints.hasOwnProperty(value) && value);

    if (!key) throw(this.msgError);

    const values = {
      key: ScreenConfig.breakpoints[key],
      current: ScreenConfig.breakpoints[this.current]
    };

    self[cache] = values.key >= values.current;

    return self[cache];
  }

  only(value: TKey) {
    const self = (this.only as any);
    const cache = `${value}_${this.current}`;

    if (self.hasOwnProperty(cache))
      return self[cache];

    const device = ScreenConfig[value];
    const key = (ScreenConfig.breakpoints.hasOwnProperty(value) && value);

    if (!key && !device)
      throw new Error((`Can't find breakpoint or device`));

    self[cache] = key ? key === this.current : device.includes(this.current);

    return self[cache];
  }

  between(prev: TKey, next: TKey) {
    const self = (this.between as any);
    const cache = `${prev}_${next}_${this.current}`;

    if (self.hasOwnProperty(cache))
      return self[cache];

    const bpPrev = ScreenConfig[prev] && ScreenConfig[prev][0];
    const keyPrev = bpPrev || (ScreenConfig.breakpoints.hasOwnProperty(prev) && prev);

    const bpNext = ScreenConfig[next] && ScreenConfig[next].slice(-1)[0];
    const keyNext = bpNext || (ScreenConfig.breakpoints.hasOwnProperty(next) && next);

    if (!keyPrev || !keyNext) throw(this.msgError);

    const values = {
      keyPrev: ScreenConfig.breakpoints[keyPrev],
      keyNext: ScreenConfig.breakpoints[keyNext],
      current: ScreenConfig.breakpoints[this.current]
    };

    self[cache] = values.keyPrev <= values.current &&  values.current <= values.keyNext;

    return self[cache];
  }
}


@Injectable({
  providedIn: 'root'
})
export class ScreenService extends Responsive {

  constructor() {
    super();
    this._init();
  }

  private _init() {
    this.update();

    window.addEventListener('resize', () => {
      this.update();
    }, false);
  }

  add(key: string) {
    if (!this[key])
      this[key] = new Responsive();

    return this[key];
  }

  get(key: string): Responsive {
    return this[key];
  }

  update() {
    const currentPrev = this.current;
    for (const key of Object.keys(ScreenConfig.breakpoints)) {
      if (ScreenConfig.breakpoints[key] > window.innerWidth)
        break;

      this.current = key as TBreakpointSystem;
    }

    if (currentPrev !== this.current)
      this.currentChanges.emit(this.current);
  }
}
